import { useState, useEffect } from "react";
import { Heart } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

interface Favorite {
  listing_id: string;
}

export default function FavoriteButton({ listingId, "data-testid": testId }: { listingId: string; "data-testid"?: string }) {
  const { user } = useAuth();
  const [favorited, setFavorited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get<Favorite[]>("/favorites/me").then(({ data }) => {
      if (!cancelled) {
        setFavorited(data.some((f) => f.listing_id === listingId));
        setChecking(false);
      }
    }).catch(() => {
      if (!cancelled) setChecking(false);
    });
    return () => { cancelled = true; };
  }, [listingId]);

  const toggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    const previous = favorited;
    try {
      const { data } = await api.post<{ favorited: boolean }>(`/favorites/${listingId}`);
      setFavorited(data.favorited);
      if (data.favorited) {
        toast.success("Adicionado aos favoritos");
      }
    } catch {
      setFavorited(previous);
      toast.error("Não foi possível atualizar favorito.");
    } finally {
      setLoading(false);
    }
  };

  if (!user || checking) return null;

  return (
    <button
      onClick={toggle}
      disabled={loading}
      data-testid={testId}
      type="button"
      className={`flex items-center justify-center rounded-full p-2 transition-colors shadow-sm ring-1 ring-white/25 ${
        favorited
          ? "bg-red-500 text-white border-transparent"
          : "bg-ink-900/85 text-cream-50 border-transparent hover:bg-ink-800"
      }`}
      aria-pressed={favorited}
      aria-label="Favorito"
    >
      <Heart className={`w-4 h-4 ${favorited ? "fill-current" : ""}`} />
    </button>
  );
}
